import { useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../context/NotificationContext';
import type { ReportDocument, ReportMeta } from '../../lib/pdf/reportModel';
import { fetchCompanyLogo } from '../../lib/pdf/companyLogo';
import { reportEditKey, resolveInitialDocument } from '../../lib/pdf/reportEdits';
import { ReportEditDialog } from './ReportEditDialog';

interface PdfExportButtonProps {
  /** Metadaten für Kopfzeile + Dateiname. */
  meta: ReportMeta;
  /**
   * Baut das Report-Dokument — wird erst beim Klick aufgerufen, damit immer der
   * aktuelle Datenstand exportiert wird.
   */
  build: () => ReportDocument;
  /** Optionaler Dateiname (ohne Endung). Default aus Projekt + Seite + Datum. */
  filename?: string;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'text' | 'outlined' | 'contained';
}

/** Macht aus beliebigem Text einen dateinamentauglichen Slug. */
function slug(s: string): string {
  return s
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'report';
}

function defaultFilename(meta: ReportMeta): string {
  const d = meta.generatedAt ?? new Date();
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return `${slug(meta.pageTitle)}_${slug(meta.projectName)}_${date}`;
}

/**
 * Wiederverwendbarer Report-Export-Button. Öffnet vor dem Export einen generischen
 * Editier-Dialog ({@link ReportEditDialog}) auf dem {@link ReportDocument} — dadurch
 * ist JEDER Report, der diesen Button einbindet, vor dem Export anpassbar (P-20),
 * mit Bearbeitungs-Persistenz über {@link resolveInitialDocument}/`writeReportEdit`
 * (LocalStorage, Muster aus StatusReportTab). Im Dialog stehen PDF- und
 * Word-/DOCX-Export (P-24) gleichberechtigt nebeneinander; beide Renderer (jsPDF
 * bzw. `docx`) werden lazy geladen, sodass sie das Haupt-Bundle nicht belasten.
 */
export function PdfExportButton({
  meta,
  build,
  filename,
  disabled,
  size = 'small',
  variant = 'outlined',
}: PdfExportButtonProps) {
  const { t } = useTranslation('common');
  const { notify } = useNotifications();
  const [dialog, setDialog] = useState<{ document: ReportDocument; editKey: string } | null>(null);

  const handleOpen = () => {
    try {
      const fresh = build();
      const editKey = reportEditKey(meta.projectName, meta.pageTitle);
      const { document } = resolveInitialDocument(editKey, fresh);
      setDialog({ document, editKey });
    } catch (err) {
      console.error('[PdfExport] Fehler beim Aufbereiten des Reports:', err);
      notify(t('pdf.error', 'Report konnte nicht geladen werden.'), 'error');
    }
  };

  const handleExportPdf = async (doc: ReportDocument) => {
    const { downloadReportPdf } = await import('../../lib/pdf/renderReport');
    // Firmenlogo des Mandanten vom Server — erst beim Export geholt, damit eine frische Änderung greift.
    // 404 = kein Logo; jeder andere Fehler darf den Export nicht verhindern.
    const logo = (await fetchCompanyLogo().catch(() => null)) ?? undefined;
    downloadReportPdf(doc, logo ? { ...meta, logo } : meta, filename ?? defaultFilename(meta));
  };

  const handleExportDocx = async (doc: ReportDocument) => {
    const { downloadReportDocx } = await import('../../lib/pdf/renderReportDocx');
    await downloadReportDocx(doc, meta, filename ?? defaultFilename(meta));
  };

  return (
    <>
      <Tooltip title={t('pdf.tooltip', 'Diese Seite bearbeiten und als PDF oder Word exportieren')}>
        <span>
          <Button
            variant={variant}
            size={size}
            color="primary"
            disabled={disabled}
            onClick={handleOpen}
            startIcon={<EditNoteIcon />}
            aria-label={t('pdf.export', 'Report exportieren')}
            data-testid="pdf-export-button"
          >
            {t('pdf.export', 'Report exportieren')}
          </Button>
        </span>
      </Tooltip>
      {dialog && (
        <ReportEditDialog
          open
          onClose={() => setDialog(null)}
          meta={meta}
          initialDocument={dialog.document}
          editKey={dialog.editKey}
          onExportPdf={handleExportPdf}
          onExportDocx={handleExportDocx}
        />
      )}
    </>
  );
}
